import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, DatePicker, Input, Table, Tag, Tooltip } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useDispatch } from "react-redux";
import { Button, Icon } from "../../../../components/Component";
import SkeletonTable, {
  SkeletonTableColumnsType,
} from "../../../../utils/skeleton-templates/table";
import { AddAccess } from "../../../../components/accessControl/AccessControl";
import { equipmentRateApis } from "../../../../api/MachineryEquipment";
import { setExportStatus } from "../../../../store/slices/misc/highlightSlice";
import { statusTagColor, statusLabel } from "../../assets/AssetStatusModal";
import RateUnitToggle from "../../shared/RateUnitToggle";
import { useHeadValue } from "../../shared/useRateUnit";
import EquipmentRateEditor from "./EquipmentRateEditor";
import RateParametersDrawer from "./RateParametersDrawer";
import EquipmentRateUploadModal from "./EquipmentRateUploadModal";
import EquipmentRatesExportModal from "./EquipmentRatesExportModal";

/**
 * Equipment Hourly Use Rate — the FLEET view, effective-dated.
 *
 * Rows are ASSETS from the Asset Master (machines are created there, under
 * Assets > Asset Master — not here). Each row carries the rate version in
 * force at the chosen "as of" instant plus its computed heads. Move the as-of
 * date and the whole table re-prices to what every machine cost on that date —
 * that is requirement 2 working end to end.
 *
 * The nine cost heads are COMPUTED SERVER-SIDE on every read, never stored:
 * change the diesel price on the Rate Parameters card and the fleet shows new
 * numbers from that card's effective instant, with nothing to migrate.
 *
 * Rendered inside MachineryNormList's tab shell, which owns the scope selector —
 * hence `projectId` as a prop. The fleet itself is org-wide; the scope picks
 * which parameter card the DRAWER edits (list pricing uses the org card).
 */

/** Decimals arrive as strings; render blanks rather than NaN. */
const money = (value: any): string => {
  if (value === undefined || value === null || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const WARNING_LABELS: Record<string, string> = {
  MISSING_LIFE_DATA: "No life data — ownership costs cannot be calculated",
  RATE_PARAMETERS_MISSING: "No rate parameters set up",
  MISSING_COST: "No equipment cost recorded",
};

const warningLabel = (code: string) => {
  if (WARNING_LABELS[code]) return WARNING_LABELS[code];
  if (code.startsWith("WAGE_MISSING:")) {
    return `No wage recorded for ${code.split(":")[1]?.toLowerCase()}`;
  }
  return code;
};

/** The nine head columns, in the order this table has always shown them. */
const HEAD_COLUMNS = [
  { key: "depreciation", title: "Depreciation", width: 120 },
  { key: "interest", title: "Interest", width: 110 },
  { key: "repair", title: "Repair", width: 110 },
  { key: "misc", title: "Misc", width: 100 },
  { key: "insurance", title: "Insurance", width: 110 },
  { key: "electricity", title: "Electricity", width: 110 },
  { key: "fuel", title: "Fuel", width: 110 },
  { key: "lubricants", title: "Lubricants", width: 110 },
  { key: "crew", title: "Crew", width: 110 },
];

const EquipmentRateList = ({
  projectId = 0,
  projectName = "",
  screenSize = 0,
}: {
  projectId?: number;
  /** Display name for the scope, so tags show "Trumpet HEP" not "Project 112". */
  projectName?: string;
  screenSize?: number;
}) => {
  const dispatch = useDispatch();

  const [dataSource, setDataSource] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [resolvedFrom, setResolvedFrom] = useState<string>("");
  /** null = "now" — live prices. A date = the fleet as it cost on that instant. */
  const [asOf, setAsOf] = useState<Dayjs | null>(null);
  /** Server-side: matches asset code, name, serial, manufacturer and model. */
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "30", "50"],
    total: 0,
  });

  const [editorOpen, setEditorOpen] = useState<{
    status: boolean;
    assetId: number | null;
  }>({ status: false, assetId: null });
  const [parametersOpen, setParametersOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  const { isPerUnit, pick, format, suffix } = useHeadValue(money);

  /**
   * The unit to name on the toggle: the single distinct output UOM on this
   * page, or a generic "unit" when the page mixes them (a fleet legitimately
   * contains Cum excavators and Rmt drills).
   */
  const pageUom = useMemo(() => {
    const units = dataSource
      .map((row) => row?.heads?.perUnit?.uomName)
      .filter((name: any): name is string => Boolean(name))
      .filter((name, index, all) => all.indexOf(name) === index);
    return units.length === 1 ? units[0] : "";
  }, [dataSource]);

  /** True when NOT ONE visible machine has an output basis — see the toggle. */
  const noPerUnitOnPage = useMemo(
    () => dataSource.every((row) => !row?.heads?.perUnit),
    [dataSource]
  );

  /**
   * One cell of a head column, in whichever unit is active. Renders an em dash
   * (via the formatters) when the per-unit view has no denominator — never the
   * ₹/hr number under a ₹/Cum heading.
   */
  const headCell = useCallback(
    (row: any, key: string) => format(pick(row?.heads, row?.heads?.perUnit, key)),
    [format, pick]
  );

  const fetchData = useCallback(
    async (params: any = {}) => {
      setLoading(true);
      try {
        // Explicit override wins over state — the as-of picker / search box
        // change and the fetch fires before the state settles.
        const at = params.asOf !== undefined ? params.asOf : asOf;
        const search =
          params.searchQuery !== undefined ? params.searchQuery : searchQuery || null;
        const current = params.current ?? 1;
        const size = params.pageSize ?? pagination.pageSize;
        const res = await equipmentRateApis.getList({
          page: current,
          pageSize: size,
          // direction "P" means PLAIN OFFSET — the backend applies no keyset
          // condition for it, so paging is driven entirely by pageDiff.
          direction: "P",
          sortOrder: "DESC",
          // pageDiff is the ZERO-BASED PAGE INDEX, not a page number: the
          // backend computes skip = pageDiff x pageSize (fetchPaginatedList in
          // src/utils/pagination.utils.ts) and ignores `page` entirely.
          // Hardcoding 0 here made every page re-fetch the first one.
          pageDiff: Math.max(0, current - 1),
          paginationId: null,
          searchQuery: search,
          ...(at ? { asOf: at.toISOString() } : {}),
        });
        if (res?.status) {
          const { list, count, resolvedFrom: from } = res.data || {};
          setDataSource(list || []);
          setResolvedFrom(from || "");
          setPagination((prev) => ({
            ...prev,
            current,
            // Persist the size too, or the pager keeps rendering the old one
            // while the server is already returning the new page length.
            pageSize: size,
            total: Number(count) || 0,
          }));
        }
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize, asOf, searchQuery]
  );

  useEffect(() => {
    fetchData({ current: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = () => fetchData({ current: pagination.current });

  const handleAsOfChange = (value: Dayjs | null) => {
    setAsOf(value);
    fetchData({ current: 1, asOf: value });
  };

  const columns: any[] = useMemo(
    () => [
      {
        title: "Equipment",
        dataIndex: "assetName",
        key: "assetName",
        fixed: "left",
        width: 250,
        render: (name: string, record: any) => (
          <div>
            <a
              href="#edit"
              onClick={(e) => {
                e.preventDefault();
                setEditorOpen({ status: true, assetId: record.assetId });
              }}
              style={{ fontWeight: 600, color: "#2557d6" }}
            >
              {name}
            </a>
            <div className="text-soft" style={{ fontSize: 11 }}>
              {record.assetCode}
              {record.toolTypeName ? ` · ${record.toolTypeName}` : ""}
            </div>
          </div>
        ),
      },
      {
        title: "Asset Status",
        dataIndex: "status",
        key: "status",
        width: 120,
        render: (status: string) => (
          <Tag color={statusTagColor(status)}>{statusLabel(status)}</Tag>
        ),
      },
      {
        // The version identity: when the rate on this row took effect, and how
        // deep the machine's price history runs.
        title: "Rate Effective From",
        key: "rateEffectiveFrom",
        width: 170,
        render: (_: any, r: any) =>
          r.hasRate ? (
            <div>
              <div>{dayjs(r.rateEffectiveFrom).format("DD MMM YYYY HH:mm")}</div>
              <div className="text-soft" style={{ fontSize: 11 }}>
                {r.versionCount} version{r.versionCount === 1 ? "" : "s"}
              </div>
            </div>
          ) : (
            <Tag color="gold">No rate yet</Tag>
          ),
      },
      // The nine head columns. Titles stay bare — the active unit is stated
      // once in the toolbar and once in the Total column, so switching basis
      // never changes the table's width.
      ...HEAD_COLUMNS.map(({ key, title, width }) => ({
        title,
        key,
        width,
        align: "right",
        render: (_: any, r: any) => headCell(r, key),
      })),
      {
        title: `Total ${suffix(pageUom)}`,
        key: "total",
        width: 140,
        align: "right",
        fixed: "right",
        render: (_: any, r: any) =>
          r.hasRate ? (
            <div
              className="d-flex align-items-center justify-content-end"
              style={{ gap: 6 }}
            >
              <strong style={{ color: "#022c57" }}>
                {headCell(r, "total")}
              </strong>
              {isPerUnit && !r?.heads?.perUnit ? (
                <Tooltip title="No Standard Output per Hour on this machine's rate — set one to see ₹ per unit of output.">
                  <Tag
                    color="gold"
                    style={{ marginInlineEnd: 0, cursor: "pointer" }}
                  >
                    no output rate
                  </Tag>
                </Tooltip>
              ) : null}
              {r?.heads?.warnings?.length ? (
                <Tooltip
                  title={
                    <ul style={{ margin: 0, paddingLeft: 16 }}>
                      {r.heads.warnings.map((w: string, i: number) => (
                        <li key={i}>{warningLabel(w)}</li>
                      ))}
                    </ul>
                  }
                >
                  <Tag color="orange" style={{ marginInlineEnd: 0, cursor: "pointer" }}>
                    !
                  </Tag>
                </Tooltip>
              ) : null}
            </div>
          ) : (
            <a
              href="#rate"
              onClick={(e) => {
                e.preventDefault();
                setEditorOpen({ status: true, assetId: r.assetId });
              }}
              style={{ fontSize: 12 }}
            >
              Set rate
            </a>
          ),
      },
    ],
    // `headCell`, `suffix`, `isPerUnit` and `pageUom` all move with the rate
    // unit — without them here the columns are captured on first render and the
    // ₹/hr <-> ₹/Cum toggle silently does nothing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [headCell, suffix, isPerUnit, pageUom]
  );

  // Drill-in editor replaces the list, exactly as NormList does for norms.
  if (editorOpen.status && editorOpen.assetId) {
    return (
      <EquipmentRateEditor
        assetId={editorOpen.assetId}
        projectId={projectId}
        projectName={projectName}
        onClose={() => setEditorOpen({ status: false, assetId: null })}
        onSaved={() => {
          setEditorOpen({ status: false, assetId: null });
          refresh();
        }}
      />
    );
  }

  return (
    <div className="card-inner card-inner-lg p-0">
      {resolvedFrom === "NONE" && (
        <Alert
          type="warning"
          showIcon
          className="mx-2 mt-2"
          message="Rate parameters are not set up"
          description="Fuel, electricity and crew costs will show as zero until you set the diesel price, electricity rate and crew wages."
          action={
            <Button
              size="sm"
              color="primary"
              onClick={() => setParametersOpen(true)}
            >
              Set them up
            </Button>
          }
        />
      )}

      {/* The where-machines-come-from hint lives on the shell's tab bar
          (MachineryNormList tabBarExtraContent), right-aligned with the tabs. */}

      {/* Toolbar: a proper full-size search bar on the left, tools on the right. */}
      <div
        className="nk-ibx-head p-0 m-0 mt-2 w-100 d-flex justify-content-between align-items-center flex-wrap"
        style={{ minHeight: "40px", border: "0px", gap: 8 }}
      >
        <div className="ms-2" style={{ flex: "1 1 260px", maxWidth: 360 }}>
          <Input.Search
            allowClear
            placeholder="Search code, name, serial, make or model…"
            onSearch={(value) => {
              setSearchQuery(value);
              fetchData({ current: 1, searchQuery: value || null });
            }}
          />
        </div>
        <ul className="nk-ibx-head-tools g-1 m-0 p-0 d-flex justify-content-end align-items-center">
          <li className="pt-0 pb-0 d-flex align-items-center" style={{ gap: 6 }}>
            <span className="text-soft" style={{ fontSize: 12 }}>
              Basis
            </span>
            {/* Stays enabled while ANY machine on the page has an output basis —
                a mixed page is the normal state until the fleet is filled in. */}
            <RateUnitToggle
              uomLabel={pageUom}
              disabled={noPerUnitOnPage}
              disabledReason="No machine on this page has a Standard Output per Hour. Set one in a machine's rate to cost it per unit of output."
            />
          </li>
          <li className="pt-0 pb-0 d-flex align-items-center" style={{ gap: 6 }}>
            <span className="text-soft" style={{ fontSize: 12 }}>
              Costs as of
            </span>
            <DatePicker
              size="small"
              value={asOf}
              onChange={handleAsOfChange}
              showTime={{ format: "HH:mm" }}
              format="DD MMM YYYY HH:mm"
              placeholder="Now"
              style={{ width: 185 }}
            />
          </li>
          <li className="pt-0 pb-0">
            <Button
              size="sm"
              color="light"
              outline
              className="btn-white"
              onClick={() => setParametersOpen(true)}
            >
              <Icon name={"setting"} />
              <span>Rate Parameters</span>
            </Button>
          </li>
          <li className="pt-0 pb-0">
            <Button
              size="sm"
              color="light"
              outline
              className="btn-white"
              onClick={() => {
                dispatch(setExportStatus("pending"));
                setExportOpen(true);
              }}
            >
              <Icon name={"download"} />
              <span>Export</span>
            </Button>
          </li>
          <li className="pt-0 pb-0">
            <AddAccess moduleName="Norms">
              <Button
                size="sm"
                color="light"
                outline
                className="btn-white"
                onClick={() => setUploadOpen(true)}
              >
                <Icon name={"upload"} />
                <span>Upload Rates</span>
              </Button>
            </AddAccess>
          </li>
        </ul>
      </div>

      <div className="content clearfix">
        {/* Full width, not the Mucking list's computed-width + auto-margin
            wrapper: that subtracts a sidebar this page doesn't have, leaving
            the table centred with dead space either side. Horizontal overflow
            is already handled by the antd scroll={{ x: "max-content" }}. */}
        <div style={{ width: "100%", margin: "10px 0", padding: "0 8px" }}>
          <SkeletonTable
            loading={loading}
            columns={columns as SkeletonTableColumnsType[]}
          >
            <Table
              rowKey="assetId"
              columns={columns}
              dataSource={dataSource}
              pagination={pagination}
              onChange={(page: any) =>
                // A page-size change restarts at page 1: keeping the old page
                // number can put the offset past the end of a now-shorter
                // list, which returns an empty table.
                page.pageSize !== pagination.pageSize
                  ? fetchData({ current: 1, pageSize: page.pageSize })
                  : fetchData({ current: page.current, pageSize: page.pageSize })
              }
              bordered
              size="middle"
              scroll={{ x: "max-content" }}
            />
          </SkeletonTable>
        </div>
      </div>

      <RateParametersDrawer
        open={parametersOpen}
        projectId={projectId}
        projectName={projectName}
        onClose={() => setParametersOpen(false)}
        onSaved={refresh}
      />
      <EquipmentRateUploadModal
        open={uploadOpen}
        projectId={projectId}
        onClose={() => setUploadOpen(false)}
        onSuccess={refresh}
      />
      <EquipmentRatesExportModal
        open={exportOpen}
        projectId={projectId}
        onClose={() => setExportOpen(false)}
      />
    </div>
  );
};

export default EquipmentRateList;
